import React, { useContext, useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from 'styled-components';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../newStudent/ProviderNewStudent';
import * as S from './styles';

interface Props {
  url?: string;
}

const SOUNDCLOUD_HOSTS = ['soundcloud.com', 'snd.sc'];
const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

function isSoundCloudUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url, window.location.origin);

    return SOUNDCLOUD_HOSTS.some(
      (host) => hostname === host || hostname.endsWith(`.${host}`),
    );
  } catch {
    return false;
  }
}

function buildPlaceholderPeaks(seed: string, bars = 160): number[] {
  let hash = 0;

  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }

  return Array.from({ length: bars }, (_, index) => {
    hash = (hash * 1664525 + 1013904223 + index) >>> 0;
    const wave = 0.35 + Math.abs(Math.sin(index / 7)) * 0.4;

    return wave * (0.55 + (hash % 45) / 100);
  });
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '00:00';
  }

  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  const clock = `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;

  if (hours > 0) {
    return `${hours}:${clock}`;
  }

  return clock;
}

export default function AudioPlayer({ url }: Props) {
  const theme = useTheme();
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;
  const containerRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const waveRef = useRef<WaveSurfer | null>(null);
  const volumeRef = useRef(1);
  const rateRef = useRef(1);
  const stopLoadingRef = useRef<(() => void) | null>(null);
  const soundCloudReadyUrlRef = useRef<string | null>(null);
  const setIsLoadingRef = useRef(setIsLoading);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [rate, setRate] = useState(1);
  const [hasError, setHasError] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const isSoundCloud = url ? isSoundCloudUrl(url) : false;
  const waveColor = theme.colors?.disable01;
  const progressColor = theme.colors?.primary;

  setIsLoadingRef.current = setIsLoading;

  const beginLoading = () => {
    const setLoading = setIsLoadingRef.current;
    if (typeof setLoading !== 'function') {
      return () => undefined;
    }

    let settled = false;
    setLoading(true);

    return () => {
      if (settled) {
        return;
      }
      settled = true;
      setLoading(false);
    };
  };

  useEffect(() => {
    const audio = audioRef.current;
    const container = containerRef.current;
    let active = true;
    let wave: WaveSurfer | null = null;
    const stopLoading = beginLoading();
    stopLoadingRef.current = stopLoading;

    const finishLoading = () => {
      if (active) {
        setIsReady(true);
      }
      stopLoading();
    };

    setHasError(false);
    setIsReady(false);
    setIsBuffering(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    if (!url) {
      finishLoading();
      return () => {
        active = false;
        stopLoading();
      };
    }

    if (isSoundCloud) {
      if (soundCloudReadyUrlRef.current === url) {
        finishLoading();
      }

      return () => {
        active = false;
        stopLoading();
        if (stopLoadingRef.current === stopLoading) {
          stopLoadingRef.current = null;
        }
      };
    }

    if (!audio || !container || !waveColor || !progressColor) {
      finishLoading();
      return () => {
        active = false;
        stopLoading();
      };
    }

    const mountWave = () => {
      if (!active || !Number.isFinite(audio.duration) || audio.duration <= 0) {
        setHasError(true);
        finishLoading();
        return;
      }

      wave?.destroy();
      wave = WaveSurfer.create({
        container,
        media: audio,
        peaks: [buildPlaceholderPeaks(url)],
        duration: audio.duration,
        height: Math.round(container.clientHeight * 0.7),
        waveColor,
        progressColor,
        cursorColor: progressColor,
        cursorWidth: 2,
        barWidth: 3,
        barGap: 2,
        barRadius: 2,
        normalize: true,
        dragToSeek: true,
        hideScrollbar: true,
      });
      waveRef.current = wave;
      setDuration(audio.duration);
      wave.setVolume(volumeRef.current);
      wave.setPlaybackRate(rateRef.current, true);

      wave.on('timeupdate', (nextTime) => {
        if (active) {
          setCurrentTime(nextTime);
        }
      });
      wave.on('play', () => {
        if (active) {
          setIsPlaying(true);
          setIsBuffering(false);
        }
      });
      wave.on('pause', () => {
        if (active) {
          setIsPlaying(false);
        }
      });
      wave.on('finish', () => {
        if (active) {
          setIsPlaying(false);
        }
      });
      wave.on('error', (error) => {
        if (!active || error?.name === 'AbortError') {
          return;
        }

        setHasError(true);
        finishLoading();
      });
      finishLoading();
    };

    const onMediaError = () => {
      if (!active) {
        return;
      }
      setHasError(true);
      finishLoading();
    };

    audio.addEventListener('error', onMediaError);

    if (audio.readyState >= 1) {
      mountWave();
    } else {
      audio.addEventListener('loadedmetadata', mountWave);
    }

    return () => {
      active = false;
      stopLoading();
      if (stopLoadingRef.current === stopLoading) {
        stopLoadingRef.current = null;
      }
      audio.removeEventListener('loadedmetadata', mountWave);
      audio.removeEventListener('error', onMediaError);
      waveRef.current = null;
      wave?.destroy();
    };
  }, [url, isSoundCloud, waveColor, progressColor]);

  if (!url) {
    return null;
  }

  if (isSoundCloud) {
    return (
      <S.Container>
        <S.Stage>
          <S.SoundCloudFrame
            title="Audiobook"
            allow="autoplay"
            src={url}
            $visible={isReady}
            onLoad={() => {
              soundCloudReadyUrlRef.current = url;
              setIsReady(true);
              stopLoadingRef.current?.();
            }}
          />
          {!isReady && (
            <S.LoadingBox>
              <CircularProgress sx={{ color: theme.colors?.primary }} />
              <S.LoadingText>Carregando áudio</S.LoadingText>
            </S.LoadingBox>
          )}
        </S.Stage>
      </S.Container>
    );
  }

  const handlePlay = () => {
    const wave = waveRef.current;
    const audio = audioRef.current;
    if (!wave || !audio) {
      return;
    }

    if (!audio.paused) {
      wave.pause();
      return;
    }

    const waitingForData = audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA;
    const stopBuffering = waitingForData ? beginLoading() : () => undefined;
    if (waitingForData) {
      setIsBuffering(true);
    }

    void Promise.resolve(wave.play())
      .then(() => {
        setIsBuffering(false);
        stopBuffering();
      })
      .catch((error: { name?: string }) => {
        setIsBuffering(false);
        stopBuffering();
        if (error?.name !== 'AbortError') {
          setHasError(true);
        }
      });
  };

  const handleVolume = (value: number) => {
    volumeRef.current = value;
    setVolume(value);
    waveRef.current?.setVolume(value);
  };

  const handleRate = (value: number) => {
    rateRef.current = value;
    setRate(value);
    waveRef.current?.setPlaybackRate(value, true);
  };

  return (
    <S.Container>
      <S.HiddenAudio ref={audioRef} preload="metadata" src={url} />
      <S.Player>
        <S.Stage>
          <S.Wave ref={containerRef} $hidden={hasError} />
          {(!isReady || isBuffering) && !hasError && (
            <S.LoadingBox>
              <CircularProgress sx={{ color: theme.colors?.primary }} />
              <S.LoadingText>Carregando áudio</S.LoadingText>
            </S.LoadingBox>
          )}
        </S.Stage>
        {hasError ? (
          <S.NativeAudio controls preload="metadata" src={url} />
        ) : (
          isReady && (
          <S.Controls>
            <S.PlayButton
              type="button"
              onClick={handlePlay}
              aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </S.PlayButton>
            <S.Time>
              {formatTime(currentTime)} / {formatTime(duration)}
            </S.Time>
            <S.Label>
              Volume
              <S.Range
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={volume}
                aria-label="Volume"
                onChange={(event) => handleVolume(Number(event.target.value))}
              />
            </S.Label>
            <S.Label>
              Velocidade
              <S.Select
                value={rate}
                aria-label="Velocidade"
                onChange={(event) => handleRate(Number(event.target.value))}
              >
                {PLAYBACK_RATES.map((item) => (
                  <option key={item} value={item}>
                    {item}x
                  </option>
                ))}
              </S.Select>
            </S.Label>
          </S.Controls>
          )
        )}
      </S.Player>
    </S.Container>
  );
}
